import React from 'react';
import { useTranslation } from 'react-i18next';
import { AggregateCommandButton, DataTable, HStack, Snackbar, ToggleButtons, ROW_ACTION_ICON } from '@lib/ui-components';
import { CollectionRequestStatus } from '../../../libs/api-interface/models/collection-request/collection-request-status.enum';
import { DownloadCardButton } from '@lib/common-sdk';
import { CancelCollectionRequestButton } from '../../shared/collection-requests/cancel-collection-request-button';
import { DeleteCollectionRequestButton } from './delete-collection-request-button';

export const CollectionRequestsTab = () => {
  const { t } = useTranslation();
  const [activePage, setActivePage] = React.useState(0);
  const [tableReloadKey, setTableReloadKey] = React.useState(1);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  function handleChangeActivePage(idx: number) {
    setActivePage(idx);
    setTableReloadKey(tableReloadKey + 1);
  }

  function renderCommentButton(row: any) {
    return (
      <AggregateCommandButton
        aggregateName={'CollectionRequestAggregate'}
        aggregateId={row.collectionRequestId}
        commandName={'comment'}
        startIcon={ROW_ACTION_ICON.comment}
        buttonLabel={t('comment')}
        dialogTitle={t('edit')}
        variant='outlined'
        fields={[
          {
            field: 'comment',
            label: t('comment'),
            type: 'text',
            defaultValue: row.comment,
          },
        ]}
        onSaved={async () => setTableReloadKey(tableReloadKey + 1)}
      />
    );
  }

  function renderDownloadCardButton(row: any) {
    return <DownloadCardButton collectionRequestId={row.collectionRequestId} />;
  }

  const fetchUrl = activePage !== 0 ? '/api/resource/collectionRequestViews' : undefined;
  const fetchFilters =
    activePage !== 0
      ? {
          active: false,
        }
      : undefined;
  const fetchPostUrl = activePage === 0 ? '/api/collection-request/list/active' : undefined;

  return (
    <>
      <HStack align='center'>
        <ToggleButtons labels={[t('open'), t('history')]} activeIndex={activePage} onChange={handleChangeActivePage} />
      </HStack>
      <DataTable
        excel={true}
        refreshKey={tableReloadKey}
        fetchUrl={fetchUrl}
        fetchFilters={fetchFilters}
        fetchPostUrl={fetchPostUrl}
        contentMaxHeightAsAlmostOneScreen={true}
        initialSort={{ columnIndex: 0, ascending: false }}
        columns={[
          {
            key: 'creationDate',
            label: t('reportDate'),
            type: 'date',
            dateFormat: 'dd.MM.yyyy / HH:mm',
          },
          {
            key: 'collectionDate',
            label: t('pickupDate'),
            type: 'date',
          },
          {
            key: 'clientName',
            label: t('client'),
            type: 'text',
            minWidth: '15rem',
          },
          {
            key: 'projectNumber',
            label: t('projectNumber'),
            type: 'text',
          },
          {
            key: 'branchName',
            label: t('branch'),
            type: 'text',
            minWidth: '8rem',
          },
          {
            key: 'branchRegistrationAddress.town',
            label: t('branchCity'),
            type: 'text',
          },
          {
            key: 'branchRegistrationAddress.street',
            label: t('branchStreet'),
            type: 'text',
            minWidth: '12rem',
          },
          {
            key: 'wasteCode',
            label: t('wasteCode'),
            type: 'text',
            minWidth: '15rem',
          },
          {
            key: 'wasteName',
            label: t('wasteName'),
            type: 'text',
            minWidth: '15rem',
          },
          {
            key: 'wasteDescription',
            label: t('wasteDescription'),
            type: 'text',
            minWidth: '15rem',
          },
          {
            key: 'type',
            label: t('concerns'),
            type: 'enum',
            enumValues: [
              { value: 'PICKUP', label: t('collectionRequestType.PICKUP') },
              { value: 'SCHEDULE', label: t('collectionRequestType.SCHEDULE') },
              { value: 'NO_PICKUP', label: t('collectionRequestType.NO_PICKUP') },
              { value: 'EXCEED', label: t('collectionRequestType.EXCEED') },
            ],
            minWidth: '10rem',
          },
          {
            key: 'mass',
            label: t('massMg'),
            type: 'numeric',
          },
          {
            key: 'container',
            label: t('collectionMethod'),
            type: 'text',
          },
          {
            key: 'containers',
            label: t('numberOfContainers'),
            type: 'text',
          },
          {
            key: 'volume',
            label: t('volumeM3'),
            type: 'numeric',
          },
          {
            key: 'comment',
            label: t('comment'),
            type: 'text',
            minWidth: '12rem',
          },
          {
            key: 'status',
            label: t('noPickup'),
            type: 'boolean',
            align: 'center',
            expressionForBooleanType: (row) => row.status === CollectionRequestStatus.MISSED,
          },
          {
            key: 'missedPickupReportedBy',
            label: t('reported'),
            type: 'text',
            joinedColumns: [
              {
                key: 'missedPickupReportDate',
                label: '',
                type: 'date',
              },
            ],
          },
          {
            key: 'exceedContainers',
            label: t('collectionRequestType.EXCEED'),
            type: 'numeric',
          },
          {
            key: 'exceedReportedBy',
            label: t('reported'),
            type: 'text',
            joinedColumns: [
              {
                key: 'exceedReportDate',
                label: '',
                type: 'date',
              },
            ],
          },
          {
            key: 'cancelledBy',
            label: t('cancelledBy'),
            type: 'text',
            joinedColumns: [
              {
                key: 'cancelDate',
                label: t('cancelDate'),
                type: 'date',
                dateFormat: 'dd.MM.yyyy / HH:mm',
              },
            ],
          },
          {
            key: 'cancelReason',
            label: t('cancellationReason'),
            type: 'text',
            minWidth: '12rem',
          },
          {
            key: 'createdBy',
            label: t('user'),
            type: 'text',
          },
          {
            key: 'emailsString',
            label: t('orderEmails'),
            type: 'text',
            minWidth: '15rem',
          },
          {
            key: 'subcontractor',
            label: t('subcontractor'),
            type: 'text',
          },
          {
            key: 'transporter',
            label: t('transportEntity'),
            type: 'text',
          },
        ]}
        rowOptions={
          activePage === 0
            ? [
                {
                  renderer: (row) => (
                    <AggregateCommandButton
                      aggregateName={'CollectionRequestAggregate'}
                      aggregateId={row.collectionRequestId}
                      commandName={'complete'}
                      startIcon={ROW_ACTION_ICON.done}
                      buttonLabel={t('done')}
                      color='secondary'
                      variant='contained'
                      forceConfirmation={true}
                      onSaved={async () => {
                        setTableReloadKey(tableReloadKey + 1);
                        setSuccessMessage(t('collectionRequestDoneMessage'));
                      }}
                    />
                  ),
                },
                {
                  renderer: (row) => (
                    <CancelCollectionRequestButton
                      collectionRequestId={row.collectionRequestId}
                      variant='organization'
                      afterSubmit={async () => {
                        setTableReloadKey(tableReloadKey + 1);
                        setSuccessMessage(t('collectionRequestCancelledMessage'));
                      }}
                    />
                  ),
                },
                {
                  renderer: renderCommentButton,
                },
                {
                  renderer: renderDownloadCardButton,
                },
              ]
            : [
                {
                  // anulowanie ustawia active=false, więc anulowane zgłoszenia są wyłącznie na liście historii
                  renderer: (row) => (
                    <DeleteCollectionRequestButton
                      collectionRequestId={row.collectionRequestId}
                      disabled={row.status !== CollectionRequestStatus.CANCELLED}
                      afterSubmit={async () => {
                        setTableReloadKey(tableReloadKey + 1);
                        setSuccessMessage(t('collectionRequestDeletedMessage'));
                      }}
                    />
                  ),
                },
                {
                  renderer: renderCommentButton,
                },
                {
                  renderer: renderDownloadCardButton,
                },
              ]
        }
      />
      <Snackbar open={!!successMessage} message={successMessage ?? ''} severity='success' onClose={() => setSuccessMessage(null)} />
    </>
  );
};
